class GiftModal extends HTMLElement {
  constructor() {
    super();
    this._autoOpenedInDrawerSession = false;
    this._autoOpenFetching = false;
    this._dismissedByUser = false;
    this._suppressAutoOpenUntilClose = false;
    this._lastFreeGiftInCart = this.getLastFreeGiftFlag();
    this._lastCartProgress = null;
    this._thresholdCrossedSinceOpen = false;
    this._drawerOpenedAt = null;
    this._autoOpenUpdateTimer = null;
    this._forcedCooldownUntil = this.getForcedCooldownUntil();

    this._handleDrawerOpen = this.onDrawerOpen.bind(this);
    this._handleDrawerClose = this.onDrawerClose.bind(this);
    this._handleKeyUp = this.onKeyUp.bind(this);
    this._handleCloseClick = this.onCloseClick.bind(this);
  }

  connectedCallback() {
    this._dismissedByUser = this.getDismissedByUser();

    document.addEventListener('cart-drawer:open', this._handleDrawerOpen);
    document.addEventListener('cart-drawer:close', this._handleDrawerClose);
    document.addEventListener('keyup', this._handleKeyUp);

    this.querySelectorAll('.js-close-goodie-popup').forEach((button) => {
      button.addEventListener('click', this._handleCloseClick);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.maybeAutoOpenFromCartUpdate(event?.cartData);
        this.scheduleAutoOpenCheckAfterUpdate();
      });
    }

    if (this.isDrawerOpen()) {
      this.onDrawerOpen();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('cart-drawer:open', this._handleDrawerOpen);
    document.removeEventListener('cart-drawer:close', this._handleDrawerClose);
    document.removeEventListener('keyup', this._handleKeyUp);

    this.querySelectorAll('.js-close-goodie-popup').forEach((button) => {
      button.removeEventListener('click', this._handleCloseClick);
    });

    if (this._cartUpdateUnsubscriber) {
      this._cartUpdateUnsubscriber();
    }

    if (this._autoOpenUpdateTimer) {
      clearTimeout(this._autoOpenUpdateTimer);
      this._autoOpenUpdateTimer = null;
    }
  }

  get autoOpenEnabled() {
    return this.dataset.autoOpen === "true";
  }

  get autoOpenCooldownSeconds() {
    return Number(this.dataset.autoOpenCooldownSeconds) || 0;
  }

  get autoOpenDismissOnClose() {
    return this.dataset.autoOpenDismissOnClose === 'true';
  }

  get goodieRequirement() {
    const goodieRequirement = Number(this.dataset.goodieRequirement);

    return Number.isFinite(goodieRequirement) ? goodieRequirement : Number.POSITIVE_INFINITY;
  }

  get goodieRequirementType() {
    return this.dataset.goodieRequirementType === 'weight' ? 'weight' : 'price';
  }

  getCartProgressValue(cartData) {
    if (!cartData) {
      return 0;
    }
    if (this.goodieRequirementType === 'weight') {
      return cartData.total_weight || 0;
    }
    return cartData.total_price || 0;
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    this.querySelector('.goodies-popup')?.classList.add('active');
    this.querySelector('.goodies-overlay')?.classList.add('active');
    const dialog = this.querySelector('[role="dialog"]');
    trapFocus(this, dialog || this);
  }

  hide(isUserDismiss = false) {
    if (!this.isOpen()) {
      return;
    }

    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    this.querySelector('.goodies-popup')?.classList.remove('active');
    this.querySelector('.goodies-overlay')?.classList.remove('active');
    removeTrapFocus(this.openedBy);

    if (isUserDismiss) {
      if (this.autoOpenDismissOnClose) {
        this.setDismissedByUser();
      } else {
        this._suppressAutoOpenUntilClose = true;
        this.setLastAutoOpenTimestamp(Date.now());
        this.logAutoOpen('suppress-until-close', {
          reason: 'user-dismissed'
        });
      }
    }
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  onKeyUp(event) {
    const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
    if (charCode === 'ESCAPE') {
      this.hide(true);
    }
  }

  onCloseClick(event) {
    event.preventDefault();
    this.hide(true);
  }

  async onDrawerOpen() {
    this._drawerOpenedAt = Date.now();
    this._thresholdCrossedSinceOpen = false;

    const cartData = await this.fetchCartData();
    if (cartData) {
      this.maybeAutoOpenFromCartUpdate(cartData);
    }
  }

  onDrawerClose() {
    this._autoOpenedInDrawerSession = false;
    this._suppressAutoOpenUntilClose = false;
    this._drawerOpenedAt = null;
    this._thresholdCrossedSinceOpen = false;
    this._lastCartProgress = null;
  }

  scheduleAutoOpenCheckAfterUpdate() {
    if (this._autoOpenUpdateTimer) {
      clearTimeout(this._autoOpenUpdateTimer);
    }

    this._autoOpenUpdateTimer = setTimeout(async () => {
      if (!this.isDrawerOpen() || !this.autoOpenEnabled) {
        return;
      }

      const cartData = await this.fetchCartData();
      if (cartData) {
        this.maybeAutoOpenFromCartUpdate(cartData);
      }
    }, 50);
  }

  maybeAutoOpenFromCartUpdate(cartData) {
    if (!cartData) {
      return;
    }

    const cartProgress = this.getCartProgressValue(cartData);
    const hasFreeGift = this.cartHasFreeGift(cartData);
    const requirement = this.goodieRequirement;
    const cooldownMs = this.autoOpenCooldownSeconds * 1000;
    const remainingToRequirement = Number.isFinite(requirement)
      ? Math.max(0, requirement - cartProgress)
      : null;

    if (
      this.isDrawerOpen() &&
      this._lastCartProgress !== null &&
      this._lastCartProgress < requirement &&
      cartProgress >= requirement
    ) {
      this._thresholdCrossedSinceOpen = true;
    }

    if (this._lastFreeGiftInCart === true && !hasFreeGift) {
      this._suppressAutoOpenUntilClose = true;
      this.setLastAutoOpenTimestamp(Date.now());
      this.setForcedCooldownUntil(cooldownMs);
    }

    this._lastCartProgress = cartProgress;
    this._lastFreeGiftInCart = hasFreeGift;
    this.setLastFreeGiftFlag(hasFreeGift);

    if (!this.canAutoOpen(cartProgress, hasFreeGift)) {
      return;
    }

    this._autoOpenedInDrawerSession = true;
    this.setLastAutoOpenTimestamp(Date.now());
    this.show();
  }

  canAutoOpen(cartProgress, hasFreeGift) {
    if (!this.isDrawerOpen()) {
      return false;
    }
    if (!this.autoOpenEnabled) {
      return false;
    }
    if (this.isOpen()) {
      return false;
    }
    if (this._autoOpenedInDrawerSession) {
      return false;
    }
    if (this._dismissedByUser) {
      return false;
    }
    if (this._suppressAutoOpenUntilClose) {
      return false;
    }
    if (cartProgress < this.goodieRequirement) {
      return false;
    }
    if (hasFreeGift) {
      return false;
    }

    const forcedCooldownRemainingMs = this.getForcedCooldownRemainingMs();
    if (forcedCooldownRemainingMs > 0) {
      return false;
    }

    const cooldownMs = this.autoOpenCooldownSeconds * 1000;
    const lastTimestamp = this.getLastAutoOpenTimestamp();
    const cooldownActive = cooldownMs > 0 && lastTimestamp && Date.now() - lastTimestamp < cooldownMs;
    if (cooldownActive && !this._thresholdCrossedSinceOpen) {
      return false;
    }
    return true;
  }

  cartHasFreeGift(cartData) {
    if (!Array.isArray(cartData.items)) {
      return false;
    }

    return cartData.items.some((item) => {
      if (!item || !item.properties) {
        return false;
      }
      return item.properties._gift_product === true || item.properties._gift_product === 'true';
    });
  }

  async fetchCartData() {
    if (this._autoOpenFetching) {
      return null;
    }

    this._autoOpenFetching = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`);
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } finally {
      this._autoOpenFetching = false;
    }
  }

  isDrawerOpen() {
    const drawer = document.getElementById('CartDrawer');
    return !!drawer && drawer.classList.contains('active');
  }

  getLastAutoOpenTimestamp() {
    try {
      const value = Number(window.sessionStorage.getItem('goodie-auto-open-last-ts'));
      return Number.isFinite(value) ? value : 0;
    } catch (error) {
      return 0;
    }
  }

  setLastAutoOpenTimestamp(value) {
    try {
      window.sessionStorage.setItem('goodie-auto-open-last-ts', String(value));
    } catch (error) {
      return;
    }
  }

  getDismissedByUser() {
    if (!this.autoOpenDismissOnClose) {
      return false;
    }
    try {
      return window.sessionStorage.getItem('goodie-auto-open-dismissed') === 'true';
    } catch (error) {
      return false;
    }
  }

  setDismissedByUser() {
    if (!this.autoOpenDismissOnClose) {
      return;
    }
    this._dismissedByUser = true;
    try {
      window.sessionStorage.setItem('goodie-auto-open-dismissed', 'true');
    } catch (error) {
      return;
    }
  }

  getForcedCooldownRemainingMs() {
    if (!this._forcedCooldownUntil) {
      return 0;
    }

    const remaining = this._forcedCooldownUntil - Date.now();
    if (remaining <= 0) {
      this._forcedCooldownUntil = 0;
      this.clearForcedCooldownUntil();
      return 0;
    }

    return remaining;
  }

  getForcedCooldownUntil() {
    try {
      const value = Number(window.sessionStorage.getItem('goodie-auto-open-forced-cooldown-until'));
      return Number.isFinite(value) ? value : 0;
    } catch (error) {
      return 0;
    }
  }

  clearForcedCooldownUntil() {
    try {
      window.sessionStorage.removeItem('goodie-auto-open-forced-cooldown-until');
    } catch (error) {
      return;
    }
  }

  setForcedCooldownUntil(cooldownMs) {
    if (!cooldownMs || cooldownMs <= 0) {
      return;
    }

    const nextUntil = Date.now() + cooldownMs;
    this._forcedCooldownUntil = Math.max(this._forcedCooldownUntil || 0, nextUntil);
    try {
      window.sessionStorage.setItem(
        'goodie-auto-open-forced-cooldown-until',
        String(this._forcedCooldownUntil)
      );
    } catch (error) {
      return;
    }
  }

  getLastFreeGiftFlag() {
    try {
      const value = window.sessionStorage.getItem('goodie-auto-open-last-free-gift');
      if (value === null) {
        return null;
      }
      return value === 'true';
    } catch (error) {
      return null;
    }
  }

  setLastFreeGiftFlag(value) {
    try {
      window.sessionStorage.setItem('goodie-auto-open-last-free-gift', value ? 'true' : 'false');
    } catch (error) {
      return;
    }
  }
}

if (!customElements.get('gift-modal')) {
  customElements.define('gift-modal', GiftModal);
}
